export type FormType = 'o1a' | 'ctp_non_fatal';

// Form Submission Types - mirrors forms-iframe O1VisaForm types
export interface ContactInfo {
  fullName: string;
  email: string;
  phoneNumber: string;
  countryOfBirth: string;
  countryOfCitizenship: string;
  linkedinUrl: string;
  resumeFileName?: string;
}

export interface ImmigrationStatus {
  currentVisaStatus: string;
  visaExpiryDate: string;
  whoIsThisFor: 'self' | 'other' | '';
  greenCardCaseStarted: 'yes' | 'no' | '';
  priorityDate: string;
}

export interface ProfessionalBackground {
  profession: string;
  yearsOfExperience: string;
  highestEducation: string;
  fieldOfStudy: string;
  fieldCategory: 'research' | 'business' | 'arts' | 'sports' | '';
}

export interface EvidenceItem {
  id: string;
  organization?: string;
  role?: string;
  title?: string;
  date?: string;
  description: string;
  url?: string;
}

export interface ExtraordinaryAbilityEvidence {
  criticalRoles: EvidenceItem[];
  judgingExperience: EvidenceItem[];
  eliteMemberships: EvidenceItem[];
  mediaCoverage: EvidenceItem[];
  scholarlyArticles: EvidenceItem[];
  awards: EvidenceItem[];
  originalContributions: EvidenceItem[];
  highSalary: EvidenceItem[];
}

export interface AdditionalDetails {
  sponsorshipStatus: 'have_sponsor' | 'looking' | 'self_sponsor' | '';
  timeline: string;
  anythingElse: string;
  referralSource: string;
}

export type CTPDocumentVerificationStatus = 'uploaded' | 'pending' | 'missing';

export interface CTPDocument {
  id: string;
  type: string;
  fileName: string;
  mimeType: string;
  uploadedAt: string;
  url?: string;
  base64?: string;
  verificationStatus: CTPDocumentVerificationStatus;
}

export interface CTPClaimIntake {
  personalDetails: Record<string, string>;
  niisqAndHistory: Record<string, string>;
  accident: Record<string, string>;
  injuriesTreatment: Record<string, string>;
  propertyDamage: Record<string, string>;
  witnesses: Array<Record<string, string>>;
  employment: Record<string, string>;
  legalRepresentation: Record<string, string>;
  settlement: Record<string, string>;
}

export interface CTPFieldStatus {
  fieldId: string;
  label: string;
  value: string;
  isFilled: boolean;
  source: 'questionnaire';
  verifiedByDocs?: boolean;
}

export interface CTPPdfOverlayRect {
  fieldId: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CTPSyncMeta {
  syncedAt: string;
  sourceLabel: string;
  caseRef?: string;
}

export interface CTPInferredMeta {
  claimantName: string;
  detectedDocuments: number;
}

export interface CTPPayload {
  intake: CTPClaimIntake;
  documents: CTPDocument[];
  fieldStatus: CTPFieldStatus[];
  pdfOverlayMap: CTPPdfOverlayRect[];
  integrationSource?: 'google_drive_demo';
  syncMeta?: CTPSyncMeta;
  inferredMeta?: CTPInferredMeta;
}

export interface FormSubmission {
  id: string;
  formType?: FormType;
  visaType: string;
  status: 'pending' | 'in_review' | 'approved' | 'needs_info';
  qualification: 'yes' | 'no' | 'maybe';
  aiSummary: string;
  submittedAt: string;
  contact: ContactInfo;
  immigration: ImmigrationStatus;
  professional: ProfessionalBackground;
  evidence: ExtraordinaryAbilityEvidence;
  additional: AdditionalDetails;
  ctp?: CTPPayload;
}

export type EvidenceCategoryKey = keyof ExtraordinaryAbilityEvidence;

export const EVIDENCE_CATEGORY_LABELS: Record<EvidenceCategoryKey, string> = {
  criticalRoles: 'Critical/Leadership Roles',
  judgingExperience: 'Judging Experience',
  eliteMemberships: 'Elite Memberships',
  mediaCoverage: 'Press & Media Coverage',
  scholarlyArticles: 'Scholarly Articles & Publications',
  awards: 'Awards & Honors',
  originalContributions: 'Original Contributions',
  highSalary: 'High Salary Evidence',
};

export const EVIDENCE_CATEGORY_ICONS: Record<EvidenceCategoryKey, string> = {
  criticalRoles: '👔',
  judgingExperience: '⚖️',
  eliteMemberships: '🏅',
  mediaCoverage: '📰',
  scholarlyArticles: '📚',
  awards: '🏆',
  originalContributions: '💡',
  highSalary: '💰',
};

export const DEFAULT_CTP_INTAKE: CTPClaimIntake = {
  personalDetails: {},
  niisqAndHistory: {},
  accident: {},
  injuriesTreatment: {},
  propertyDamage: {},
  witnesses: [],
  employment: {},
  legalRepresentation: {},
  settlement: {},
};
